import { Camera, CheckCircle2, Printer } from "lucide-react";
import { useState } from "react";
import type { Watch } from "../../types";
import { CameraView } from "./Camera";
import { Modal } from "./Modal";
import { useTicketContext } from "./useTicket";

type Step = { kind: "camera"; retake?: string } | { kind: "review"; watchId: string } | { kind: "finish" };

/**
 * The check-in, start to finish in one window (Joe, 2026-11-03):
 *   photo of a watch → the AI fills brand, model, description and condition, staff check and save →
 *   "Take another photo" (next watch on the same ticket) or "Finish check-in" → Pending Inspection,
 *   and the check-in slip plus one work slip per watch print.
 */
export function CheckInWizard({ onClose }: { onClose: () => void }) {
  const { detail, save, saving } = useTicketContext();
  const t = detail.ticket;
  // The check-in starts with the first watch's photo (Joe, 2026-11-03).
  const [step, setStep] = useState<Step>({ kind: "camera" });
  const [notice, setNotice] = useState<string | null>(null);
  const [desc, setDesc] = useState(t.description ?? "");
  const [spareChoice, setSpareChoice] = useState<"return" | "new" | "">("");
  const needsSpareChoice = !!detail.spareFrom && !t.returnOfTicketNo;

  const photo = async (data: string) => {
    const retake = step.kind === "camera" ? step.retake : undefined;
    const d = await save("POST", "/photos", { data, contentType: "image/jpeg", identify: true, ...(retake ? { watchId: retake } : { newWatch: true }) }) as
      (Awaited<ReturnType<typeof save>> & { photoResult?: { watchId: string | null } }) | null;
    const id = d?.photoResult?.watchId ?? retake;
    setNotice(d?.notice ?? null);
    if (id) setStep({ kind: "review", watchId: id });
  };

  const finish = async () => {
    const tab = window.open("", "_blank"); // opened now so the browser doesn't block it
    const d = await save("POST", "/check-in", {
      description: desc, productCount: detail.watches.length || t.productCount, brands: t.brands, expectedUpdatedAt: t.updatedAt,
      ...(needsSpareChoice ? { spareChoice } : {}),
    });
    if (d && tab) { tab.location.href = `/print/checkin/${t.ticketNo}`; onClose(); } else tab?.close();
  };

  const title = step.kind === "camera" ? `Check-in #${t.ticketNo}: photo of watch ${step.retake ? "(retake)" : detail.watches.length + 1}`
    : step.kind === "review" ? `Check-in #${t.ticketNo}: check the details` : `Check-in #${t.ticketNo}: finish`;

  return (
    <Modal title={title} onClose={onClose}>
      <div className="wizard">
        <ol className="wizard-watches" aria-label="Watches on this ticket">
          {detail.watches.map((w) => (
            <li key={w.id} className={step.kind === "review" && step.watchId === w.id ? "current" : ""}>
              <strong>{t.ticketNo}-{w.position}</strong> {[w.brand, w.model].filter(Boolean).join(" ") || "watch"}
            </li>
          ))}
        </ol>

        {step.kind === "camera" && (
          <CameraView busy={saving} busyText="Saving and identifying…"
            hint="Dial facing the camera, filling the frame. The AI fills in the details for you to check." onPhoto={photo}>
            {detail.watches.length > 0 && <button type="button" className="btn outline" disabled={saving} onClick={() => setStep({ kind: "finish" })}>Finish check-in</button>}
            {!detail.watches.length && <button type="button" className="link-btn" disabled={saving} onClick={() => setStep({ kind: "finish" })}>Check in without photos</button>}
          </CameraView>
        )}

        {step.kind === "review" && (
          <Review key={step.watchId} watch={detail.watches.find((w) => w.id === step.watchId)} notice={notice}
            onRetake={() => setStep({ kind: "camera", retake: step.watchId })}
            onNext={() => { setNotice(null); setStep({ kind: "camera" }); }}
            onFinish={() => setStep({ kind: "finish" })} />
        )}

        {step.kind === "finish" && (
          <div className="mini-form">
            <label>Description (what the customer reported, condition, instructions)
              <textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} /></label>
            {needsSpareChoice && (
              <fieldset className="radios"><legend className="lbl">This package came on the spare return label from ticket #{detail.spareFrom}. Which job is it?</legend>
                <label><input type="radio" name="spare" checked={spareChoice === "return"} onChange={() => setSpareChoice("return")} /> Return job (same watch as #{detail.spareFrom})</label>
                <label><input type="radio" name="spare" checked={spareChoice === "new"} onChange={() => setSpareChoice("new")} /> New job (different watch)</label>
              </fieldset>
            )}
            <p className="hint">Moves the ticket to Pending Inspection (due in 7 days) and prints the check-in slip
              {detail.watches.length ? ` and ${detail.watches.length} work slip${detail.watches.length === 1 ? "" : "s"}` : ""} on the Zebra.</p>
            <div className="btn-row wrap">
              <button type="button" className="btn primary" disabled={saving || (needsSpareChoice && !spareChoice)} onClick={() => void finish()}>
                <Printer size={16} /> Finish check-in and print</button>
              <button type="button" className="btn outline" disabled={saving} onClick={() => setStep({ kind: "camera" })}><Camera size={16} /> Add another watch</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** One watch after its photo: the AI's suggestion, editable, then next or finish. */
function Review({ watch, notice, onRetake, onNext, onFinish }: { watch: Watch | undefined; notice: string | null; onRetake: () => void; onNext: () => void; onFinish: () => void }) {
  const { detail, save, saving, lookups } = useTicketContext();
  const [f, setF] = useState({ brand: watch?.brand ?? "", model: watch?.model ?? "", description: watch?.description ?? "", condition: watch?.condition ?? "" });
  if (!watch) return <p className="muted">Saving…</p>;
  const pic = detail.photos.find((p) => p.id === watch.photoId);
  const ai = pic?.ai;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const saveAnd = async (then: () => void) => { if (await save("PUT", `/watches/${watch.id}`, f)) then(); };
  return (
    <div className="review">
      {pic?.url && <img className="review-photo" src={pic.url} alt={`Watch ${detail.ticket.ticketNo}-${watch.position}`} />}
      <div className="mini-form">
        {notice && <p className="hint">{notice}</p>}
        {ai && ai.confidence !== "high" && <p className="warn">The AI is not sure about this one ({ai.confidence} confidence). Please check brand and model.</p>}
        <div className="two"><label>Brand<input value={f.brand} onChange={set("brand")} list="watch-brands" autoFocus /></label>
          <label>Model<input value={f.model} onChange={set("model")} /></label></div>
        <label>Description (shown to the customer on the estimate page)<textarea rows={2} value={f.description} onChange={set("description")} /></label>
        <datalist id="watch-brands">{lookups?.watchBrands.map((b) => <option key={b} value={b} />)}</datalist>
        <label>Condition<input value={f.condition} onChange={set("condition")} placeholder="Scratches, cracked crystal, missing crown…" /></label>
        <div className="btn-row wrap">
          <button type="button" className="btn primary" disabled={saving} onClick={() => void saveAnd(onNext)}><Camera size={16} /> Save, take another photo</button>
          <button type="button" className="btn green" disabled={saving} onClick={() => void saveAnd(onFinish)}><CheckCircle2 size={16} /> Save, finish check-in</button>
          <button type="button" className="link-btn" disabled={saving} onClick={onRetake}>Retake photo</button>
        </div>
      </div>
    </div>
  );
}
